import { useUser } from '@folklore/auth';
import { useIntersectionObserver } from '@folklore/hooks';
import { getComponentFromName } from '@folklore/utils';
import classNames from 'classnames';
import { format } from 'date-fns/format';
import { formatISO } from 'date-fns/formatISO';
import { parseISO } from 'date-fns/parseISO';
import { useEffect } from 'react';

import useDocumentsInfinite from '../../hooks/useDocumentsInfinite';
import { useNotificationsInfinite } from '../../hooks/useNotifications';
import { useTimelineInfinite } from '../../hooks/useTimeline';

import DocumentHorizontalCard from '../cards/DocumentHorizontalCard';
import * as NotificationComponents from '../cards/notifications';
import Time from '../partials/Time';
import SectionTitle from '../typography/SectionTitle';

import styles from '../../styles/panels/account-panel.module.css';

interface AccountTimelinePanelProps {
    className?: string;
}

function AccountTimelinePanel({ className }: AccountTimelinePanelProps) {
    const { subscriptions: userSubscriptions = [] } = useUser() || {};

    const userSubscriptionsIds = (userSubscriptions || [])
        .filter(({ subscribed = false }) => subscribed)
        .map(({ id }) => id);

    const {
        items: timelineItems = null,
        endIsReached: timelineEndIsReached,
        isFetching: timelineIsFetching,
        isFetchingNextPage: timelineIsFetchingNextPage,
        fetchNextPage: timelineFetchNextPage,
    } = useTimelineInfinite({
        has_document: true,
    });

    const {
        items: notificationsItems = null,
        endIsReached: notificationsEndIsReached,
        isFetching: notificationsIsFetching,
        isFetchingNextPage: notificationsIsFetchingNextPage,
        fetchNextPage: notificationsFetchNextPage,
    } = useNotificationsInfinite();

    const {
        items: newslettersItems = null,
        endIsReached: newslettersEndIsReached,
        isFetching: newslettersIsFetching,
        isFetchingNextPage: newslettersIsFetchingNextPage,
        fetchNextPage: newslettersFetchNextPage,
    } = useDocumentsInfinite(
        {
            type: 'newsletter',
            subscription: userSubscriptionsIds,
        },
        {
            enabled: userSubscriptionsIds.length > 0,
        },
    );

    const {
        ref: refTimelineEnd,
        entry: { isIntersecting: timelineEndInView = false },
    } = useIntersectionObserver({
        disabled: timelineIsFetching || timelineIsFetchingNextPage,
    });

    const {
        ref: refNotificationsEnd,
        entry: { isIntersecting: notificationsEndInView = false },
    } = useIntersectionObserver({
        disabled: notificationsIsFetching || notificationsIsFetchingNextPage,
    });

    const {
        ref: refNewslettersEnd,
        entry: { isIntersecting: newslettersEndInView = false },
    } = useIntersectionObserver({
        disabled: newslettersIsFetching || newslettersIsFetchingNextPage,
    });

    const shouldFetchTimeline =
        timelineEndInView &&
        !timelineEndIsReached &&
        !timelineIsFetching &&
        !timelineIsFetchingNextPage;

    const shouldFetchNotifications =
        notificationsEndInView &&
        !notificationsEndIsReached &&
        !notificationsIsFetching &&
        !notificationsIsFetchingNextPage;

    const shouldFetchNewsletters =
        newslettersEndInView &&
        !newslettersEndIsReached &&
        !newslettersIsFetching &&
        !newslettersIsFetchingNextPage;

    useEffect(() => {
        if (shouldFetchTimeline) {
            timelineFetchNextPage();
        }
        if (shouldFetchNotifications) {
            notificationsFetchNextPage();
        }
        if (shouldFetchNewsletters) {
            newslettersFetchNextPage();
        }
    }, [shouldFetchTimeline, shouldFetchNotifications, shouldFetchNewsletters]);

    const items = [
        ...(timelineItems || []).map(({ document: item, createdAt }, index) => ({
            id: item.id,
            type: 'document',
            item: item,
            date: parseISO(createdAt),
            dateKey: format(parseISO(createdAt), 'yyyy-MM-dd'),
            endRef: index === timelineItems.length - 1 ? refTimelineEnd : null,
        })),
        ...(newslettersItems || []).map((item, index) => ({
            id: item.id,
            type: 'document',
            item,
            date: parseISO(item.date),
            dateKey: format(parseISO(item.date), 'yyyy-MM-dd'),
            endRef:
                newslettersItems !== null && index === newslettersItems.length - 1
                    ? refNewslettersEnd
                    : null,
        })),
        ...(notificationsItems || []).map((item, index) => ({
            id: item.id,
            type: 'notification',
            item,
            date: parseISO(item.date),
            dateKey: format(parseISO(item.date), 'yyyy-MM-dd'),
            endRef:
                notificationsItems !== null && index === notificationsItems.length - 1
                    ? refNotificationsEnd
                    : null,
        })),
    ].sort((a, b) => (a.date.getTime() > b.date.getTime() ? -1 : 1));

    const dates = items.reduce((groups, item) => {
        const { id, type, dateKey } = item;
        const itemKey = `${type}-${id}`;
        const index = groups.findIndex(({ date }) => date === dateKey);
        const currentItems = index === -1 ? [] : groups[index].items;
        const lastItem = currentItems.length > 0 ? currentItems[currentItems.length - 1] : null;
        const lastItemKey = lastItem !== null ? `${lastItem.type}-${lastItem.id}` : null;
        const newItems = itemKey === lastItemKey ? currentItems.slice(0, -1) : currentItems;
        return [
            ...groups.slice(0, index === -1 ? groups.length : index),
            {
                date: dateKey,
                items: index === -1 ? [item] : [...newItems, item],
            },
            ...groups.slice(index === -1 ? groups.length : index + 1),
        ];
    }, []);

    return (
        <div className={classNames([styles.container, className])}>
            <div className={styles.inner}>
                {dates.map(({ date, items: dateItems }) => (
                    <div key={`date-group-${date}`} className={styles.section}>
                        <SectionTitle className={styles.title}>
                            <Time date={date} withoutRelative />
                        </SectionTitle>
                        <div className={classNames([styles.box, styles.list])}>
                            {dateItems.map((item) => {
                                const { id, type: itemType, date, endRef } = item;
                                let card;
                                if (itemType === 'notification') {
                                    const { item: notification } = item;
                                    const { type } = notification;
                                    const NotificationComponent = getComponentFromName(
                                        NotificationComponents,
                                        type,
                                    );
                                    card =
                                        NotificationComponent !== null ? (
                                            <NotificationComponent
                                                {...notification}
                                                withoutTime
                                                ratio="square"
                                                size="medium"
                                                className={styles.card}
                                            />
                                        ) : null;
                                }
                                if (itemType === 'document') {
                                    const { item: document } = item;
                                    card = (
                                        <DocumentHorizontalCard
                                            {...document}
                                            date={formatISO(date)}
                                            ratio="square"
                                            size="medium"
                                            withBrandIcon
                                            withoutTime
                                            className={styles.card}
                                        />
                                    );
                                }
                                return card !== null ? (
                                    <div
                                        key={`${itemType}-${id}`}
                                        className={classNames([styles.item, styles.timelineItem])}
                                    >
                                        {card}
                                        <Time date={date} timeOnly className={styles.date} />
                                        {endRef !== null ? (
                                            <div ref={endRef} className={styles.end} />
                                        ) : null}
                                    </div>
                                ) : null;
                            })}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default AccountTimelinePanel;
